import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { reserveNotification, releaseNotification, useChatIsOpen } from './notificationVisibility';
import { hasNotificationFormatting, renderNotificationText } from './notificationTextFormat';

interface SocialNotification {
  id: string;
  mensagem: string;
  tipo_servico: string;
  frequencia: number;
  is_active: boolean;
  social_position?: SocialPosition;
  social_display_time?: number;
  social_pause_after_close?: number;
  show_on_mobile?: boolean;
  ocorrencia_minutos?: number | null;
}

type SocialPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

const socialPositionClasses: Record<SocialPosition, string> = {
  // No smartphone: 4px da lateral e da borda inferior; mantém regras do Admin.
  'bottom-left': 'bottom-[4px] left-1 sm:bottom-6 sm:left-6',
  'bottom-right': 'bottom-[4px] right-1 sm:bottom-6 sm:right-6',
  'top-left': 'top-28 left-1 sm:left-6',
  'top-right': 'top-28 right-1 sm:right-6',
};

function getSocialPosition(value: unknown): SocialPosition {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(socialPositionClasses, value)
    ? value as SocialPosition : 'bottom-left';
}


// Exclusivo das NOTIFICAÇÕES SOCIAIS. Não consulta configurações AO VIVO.
// Valores padrão usados antes da consulta ao Supabase.
function safeDisplaySeconds(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 3 && n <= 15 ? n : 5;
}
function safePauseMinutes(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 30 ? n : 5;
}

// Apenas aparencia: nao interfere na frequencia, nas mensagens ou no Supabase.
// Classes explicitas para serem reconhecidas pelo Tailwind na compilacao.
const notificationStyles = {
  auto: { icon: 'ri-car-line', circle: 'bg-orange-50', text: 'text-orange-600', badge: 'bg-orange-50 text-orange-700', bar: 'bg-orange-500' },
  vida: { icon: 'ri-heart-pulse-line', circle: 'bg-rose-50', text: 'text-rose-600', badge: 'bg-rose-50 text-rose-700', bar: 'bg-rose-500' },
  residencial: { icon: 'ri-home-4-line', circle: 'bg-amber-50', text: 'text-amber-600', badge: 'bg-amber-50 text-amber-700', bar: 'bg-amber-500' },
  empresarial: { icon: 'ri-building-4-line', circle: 'bg-teal-50', text: 'text-teal-700', badge: 'bg-teal-50 text-teal-700', bar: 'bg-teal-500' },
  incendio: { icon: 'ri-fire-line', circle: 'bg-red-50', text: 'text-red-600', badge: 'bg-red-50 text-red-700', bar: 'bg-red-500' },
  patrimonial: { icon: 'ri-shield-check-line', circle: 'bg-emerald-50', text: 'text-emerald-600', badge: 'bg-emerald-50 text-emerald-700', bar: 'bg-emerald-500' },
  consultoria: { icon: 'ri-customer-service-2-line', circle: 'bg-violet-50', text: 'text-violet-700', badge: 'bg-violet-50 text-violet-700', bar: 'bg-violet-500' },
  default: { icon: 'ri-shield-check-line', circle: 'bg-teal-50', text: 'text-teal-700', badge: 'bg-teal-50 text-teal-700', bar: 'bg-teal-500' },
} as const;

function getNotificationStyle(service: string) {
  const normalized = (service || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (normalized.includes('consultor') || normalized.includes('assessoria')) return notificationStyles.consultoria;
  if (normalized.includes('incend')) return notificationStyles.incendio;
  if (normalized.includes('patrimon')) return notificationStyles.patrimonial;
  if (normalized.includes('empres')) return notificationStyles.empresarial;
  if (normalized.includes('residen')) return notificationStyles.residencial;
  if (normalized.includes('vida')) return notificationStyles.vida;
  if (normalized.includes('auto') || normalized.includes('veicul') || normalized.includes('frota')) return notificationStyles.auto;
  return notificationStyles.default;
}

// FORMATO VISUAL DA IBX BRASIL — nenhuma alteracao no cadastro ou no Supabase.
// No ambiente local, localidades e tempos AUSENTES do texto original podem ser
// preenchidos com dados demonstrativos para avaliar o layout. No build publico,
// esses dados ficticios nao sao acrescentados.
const DEMO_VISUAL = false; // Localidades e horários somente quando inseridos pelo administrador.

function getRegisteredMinutes(value: unknown): number | null {
  // Campo editável por mensagem no Admin. Não calcula nem alega horário real.
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 10080 ? n : null;
}

const serviceLabels: Record<string, string> = {
  'seguro-auto': 'Seguro Auto',
  'seguro-residencial': 'Seguro Residencial',
  'seguro-vida': 'Seguro de Vida',
  'seguro-empresarial': 'Seguro Empresarial',
  'seguro-patrimonial': 'Seguro Patrimonial',
  'seguro-incendio': 'Seguro Incêndio',
  'consultoria': 'Consultoria Técnica',
  'cotacao': 'Cotação',
};

interface SocialDisplay {
  identity: string;
  action: string;
  elapsed: string | null;
  badge: string;
}

const demoLocations: Record<string, string> = {
  carlos: 'Uberlândia-MG',
  fernanda: 'Belo Horizonte-MG',
  paulo: 'Brasília-DF',
  ana: 'Campinas-SP',
  roberto: 'Santos-SP',
  juliana: 'Curitiba-PR',
  ricardo: 'São Paulo-SP',
  marcos: 'Recife-PE',
  pedro: 'Porto Alegre-RS',
  'empresa tech solutions': 'Campinas-SP',
  'empresa varejo plus': 'Goiânia-GO',
  'indústria xyz': 'Joinville-SC',
  'construtora abc': 'Ribeirão Preto-SP',
};

const demoLocationPool = [
  'Uberlândia-MG', 'Curitiba-PR', 'São Paulo-SP', 'Belo Horizonte-MG',
  'Goiânia-GO', 'Porto Alegre-RS', 'Recife-PE', 'Campinas-SP',
];

const cityStates: Record<string, string> = {
  'são paulo': 'São Paulo-SP',
  'rio de janeiro': 'Rio de Janeiro-RJ',
  'brasilia': 'Brasília-DF',
  'brasília': 'Brasília-DF',
  'curitiba': 'Curitiba-PR',
  'belo horizonte': 'Belo Horizonte-MG',
  'uberlandia': 'Uberlândia-MG',
  'uberlândia': 'Uberlândia-MG',
  'campinas': 'Campinas-SP',
  'porto alegre': 'Porto Alegre-RS',
  'recife': 'Recife-PE',
  'goiânia': 'Goiânia-GO',
  'joinville': 'Joinville-SC',
  'santos': 'Santos-SP',
  'ribeirão preto': 'Ribeirão Preto-SP',
};

function normalizedKey(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
}

function seededNumber(value: string): number {
  let n = 0;
  for (const letter of value) n = (Math.imul(n, 31) + letter.charCodeAt(0)) >>> 0;
  return n;
}

function getBadge(action: string): string {
  if (/solicit|pediu|pedido|contato|orçamento|orcamento|cotação|cotacao/i.test(action)) return 'NOVO CONTATO';
  if (/economiz|reduziu|reduz|otimiz|poupou/i.test(action)) return 'RESULTADO';
  if (/contrat|adquiriu|garantiu|protegeu|renovou/i.test(action)) return 'CONTRATADO';
  return 'ATIVIDADE';
}

function getSocialDisplay(item: SocialNotification): SocialDisplay {
  const original = (item.mensagem || '').trim();
  const registeredMinutes = getRegisteredMinutes(item.ocorrencia_minutos);
  // Texto com marcadores foi personalizado manualmente no Admin: respeitar
  // exatamente a formatação escolhida, sem tentar separar nome e atividade.
  // As mensagens antigas, sem marcadores, mantêm o formato automático anterior.
  if (hasNotificationFormatting(original)) {
    const parts = original.split(/\s+\|\s+/);
    const structured = parts.length >= 2;
    const rawActionText = structured ? `${parts[0]} ${parts[1]}` : original;
    const actionText = registeredMinutes === null ? rawActionText
      : rawActionText.replace(/\s+(?:h[áa]\s+\d+\s+minutos?|agora\s+mesmo|h[áa]\s+pouco(?:s\s+minutos)?|recentemente)\.?$/i, '').trim();
    // Se a mensagem começa com um nome/localidade em negrito, mostramos essa
    // identidade na linha superior e a atividade em texto normal abaixo.
    const highlightedIdentity = actionText.match(/^\*\*([^*\n]+)\*\*\s+([\s\S]+)$/);
    const identity = highlightedIdentity ? highlightedIdentity[1].trim() : '';
    const formattedAction = highlightedIdentity ? highlightedIdentity[2].trim() : actionText;
    const explicitlyWrittenTime = /\bh[áa]\s+\d+\s+minutos?\b/i.test(actionText);
    const structuredMinutes = structured ? Number(parts[2]) : NaN;
    const explicitMinutes = Number.isInteger(structuredMinutes) && structuredMinutes >= 1 && structuredMinutes <= 9999
      ? structuredMinutes : null;
    const demoMinutes = DEMO_VISUAL && !explicitlyWrittenTime ? 18 + seededNumber(item.id || original) % 31 : null;
    const minutes = registeredMinutes ?? (explicitlyWrittenTime ? null : (explicitMinutes ?? demoMinutes));
    return {
      identity,
      action: formattedAction,
      elapsed: minutes === null ? (explicitlyWrittenTime ? null : 'Há poucos minutos') : `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`,
      badge: getBadge(formattedAction),
    };
  }
  // Formato opcional para futuros registros:
  //   Carlos, Uberlândia-MG | contratou seguro empresarial | 36
  // Nao exige colunas novas nem altera o cadastro atual.
  const segments = original.split(/\s+\|\s+/);
  const hasStructuredFields = segments.length >= 2;
  const originalElapsed = original.match(/\bh[áa]\s+(\d+)\s+minutos?\b/i);
  const structuredMinutes = hasStructuredFields ? Number(segments[2]) : NaN;
  const minutes = Number.isInteger(structuredMinutes) && structuredMinutes >= 1 && structuredMinutes <= 9999
    ? structuredMinutes
    : originalElapsed ? Number(originalElapsed[1]) : null;

  const actionText = hasStructuredFields
    ? segments[1].trim()
    : original.replace(/\s+(?:h[áa]\s+\d+\s+minutos?|agora\s+mesmo|h[áa]\s+pouco(?:s\s+minutos)?|recentemente)\.?$/i, '').trim();

  const match = hasStructuredFields
    ? null
    : actionText.match(/^(.+?)\s+((?:acabou\s+de\s+)?(?:solicitou|solicitar|contratou|contratar|economizou|economizar|reduziu|reduzir|garantiu|garantir|protegeu|proteger|otimizou|otimizar|pediu|recebeu|realizou|adquiriu|renovou|conseguiu|obteve|fez)\b[\s\S]*)$/i);

  let subject = hasStructuredFields ? segments[0].trim() : (match ? match[1].trim() : '');
  const action = hasStructuredFields ? actionText : (match ? match[2].trim() : original);
  const cleanedAction = registeredMinutes === null ? action
    : action.replace(/\s+(?:h[áa]\s+\d+\s+minutos?|agora\s+mesmo|h[áa]\s+pouco(?:s\s+minutos)?|recentemente)\.?$/i, '').trim();
  let location: string | null = null;

  if (subject) {
    // Cidade/UF digitada explicitamente na mensagem — nao e demonstrativa.
    const explicitlyFormatted = subject.match(/^(.+?),\s*([^,]+-[A-Z]{2})$/);
    if (explicitlyFormatted) {
      subject = explicitlyFormatted[1].trim();
      location = explicitlyFormatted[2].trim();
    } else {
      const locationWritten = subject.match(/^(.+?)\s+de\s+(São Paulo|Rio de Janeiro|Brasília|Brasilia|Curitiba|Belo Horizonte|Uberlândia|Uberlandia|Campinas|Porto Alegre|Recife|Goiânia|Joinville|Santos|Ribeirão Preto)$/i);
      if (locationWritten) {
        subject = locationWritten[1].trim();
        location = cityStates[locationWritten[2].toLowerCase()] || null;
      }
    }
    if (!location && DEMO_VISUAL) {
      // Somente localhost / modo desenvolvimento: preenche localidade ilustrativa.
      const seed = seededNumber(item.id || item.mensagem || subject);
      location = demoLocations[normalizedKey(subject)] || demoLocationPool[seed % demoLocationPool.length];
    }
  }

  // Minutos ausentes sao simulados APENAS durante o teste local, de modo estavel.
  const shownMinutes = registeredMinutes ?? minutes ?? (DEMO_VISUAL ? 18 + seededNumber(item.id || original) % 31 : null);
  return {
    identity: subject ? `${subject}${location ? `, ${location}` : ''}` : 'Atualização IX Brasil',
    action: cleanedAction,
    elapsed: shownMinutes === null ? 'Há poucos minutos' : `há ${shownMinutes} ${shownMinutes === 1 ? 'minuto' : 'minutos'}`,
    badge: getBadge(cleanedAction),
  };
}

const EXIT_ANIMATION_MS = 1000;
const START_AFTER_SCROLL_MS = 2000;

export default function SocialProofNotifications() {
  const chatOpen = useChatIsOpen();
  const [notifications, setNotifications] = useState<SocialNotification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<SocialNotification | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const pauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastIndex = useRef(-1);
  const ownedRef = useRef(false);
  const closingManuallyRef = useRef(false);

  // Tela com largura inferior a 640px: mesma faixa usada pelo Tailwind antes de sm:.
  // Nenhuma classe CSS nova e nenhum bloqueio para tablets/computadores.
  const [isSmartphone, setIsSmartphone] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const syncDevice = () => setIsSmartphone(mediaQuery.matches);
    syncDevice();
    mediaQuery.addEventListener('change', syncDevice);
    return () => mediaQuery.removeEventListener('change', syncDevice);
  }, []);
  const mobileAllowed = !isSmartphone || notifications[0]?.show_on_mobile !== false;

  // Ao desativar no celular, limpar o aviso e liberar a alternância imediatamente.
  useEffect(() => {
    if (!mobileAllowed) {
      setIsVisible(false);
      setIsMounted(false);
      if (ownedRef.current) {
        releaseNotification('social');
        ownedRef.current = false;
      }
    }
  }, [mobileAllowed]);

  // A primeira rolagem libera a exibição após 2 segundos.
  // A visita inicial, sem rolagem, não dispara notificações.
  useEffect(() => {
    let readyTimer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (window.scrollY < 10 || readyTimer) return;
      window.removeEventListener('scroll', onScroll);
      readyTimer = setTimeout(() => setHasScrolled(true), START_AFTER_SCROLL_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (readyTimer) clearTimeout(readyTimer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('ibx_social_notifications')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (mounted) setNotifications(data || []);
      } catch (error) {
        logger.error('Erro ao carregar notificações sociais:', error);
      }
    };
    void loadNotifications();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!hasScrolled || isPaused || chatOpen || notifications.length === 0 || !mobileAllowed) return;
    let nextTimer: ReturnType<typeof setTimeout> | undefined;
    let enterTimer: ReturnType<typeof setTimeout> | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let exitTimer: ReturnType<typeof setTimeout> | undefined;
    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;
    const displayMs = safeDisplaySeconds(notifications[0]?.social_display_time) * 1000;

    const showNext = () => {
      if (cancelled || closingManuallyRef.current) return;
      // Não permitir a sobreposição com os avisos AO VIVO.
      if (!reserveNotification('social')) {
        nextTimer = setTimeout(showNext, 600);
        return;
      }
      ownedRef.current = true;
      const chosenIndex = notifications.length > 1
        ? (lastIndex.current + 1 + Math.floor(Math.random() * (notifications.length - 1))) % notifications.length
        : 0;
      lastIndex.current = chosenIndex;
      const notification = notifications[chosenIndex];
      setCurrentNotification(notification);
      setIsMounted(true);
      setIsVisible(false);
      setProgress(100);
      // Duas pinturas distintas permitem a transição de entrada ser percebida.
      enterTimer = setTimeout(() => setIsVisible(true), 40);
      const startedAt = Date.now();
      progressInterval = setInterval(() => {
        setProgress(Math.max(0, 100 - ((Date.now() - startedAt) / displayMs) * 100));
      }, 100);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        if (progressInterval) clearInterval(progressInterval);
        // Não desmontar imediatamente: esperar a saída terminar.
        exitTimer = setTimeout(() => {
          setIsMounted(false);
          releaseNotification('social');
          ownedRef.current = false;
          const frequency = Number(notification.frequencia);
          const seconds = Number.isFinite(frequency) ? Math.min(60, Math.max(3, frequency)) : 15;
          nextTimer = setTimeout(showNext, seconds * 1000);
        }, EXIT_ANIMATION_MS);
      }, displayMs);
    };

    nextTimer = setTimeout(showNext, 0);
    return () => {
      cancelled = true;
      if (nextTimer) clearTimeout(nextTimer);
      if (enterTimer) clearTimeout(enterTimer);
      if (hideTimer) clearTimeout(hideTimer);
      if (exitTimer) clearTimeout(exitTimer);
      if (progressInterval) clearInterval(progressInterval);
      if ((!closingManuallyRef.current || chatOpen) && ownedRef.current) {
        releaseNotification('social');
        ownedRef.current = false;
      }
    };
  }, [notifications, isPaused, hasScrolled, chatOpen, mobileAllowed]);

  useEffect(() => () => {
    if (pauseTimer.current) clearTimeout(pauseTimer.current);
    if (manualExitTimer.current) clearTimeout(manualExitTimer.current);
    releaseNotification('social');
  }, []);

  const handleClose = () => {
    if (closingManuallyRef.current) return;
    closingManuallyRef.current = true;
    setIsVisible(false);
    setIsPaused(true);
    if (manualExitTimer.current) clearTimeout(manualExitTimer.current);
    manualExitTimer.current = setTimeout(() => {
      setIsMounted(false);
      releaseNotification('social');
      ownedRef.current = false;
      closingManuallyRef.current = false;
    }, EXIT_ANIMATION_MS);
    if (pauseTimer.current) clearTimeout(pauseTimer.current);
    pauseTimer.current = setTimeout(() => setIsPaused(false), safePauseMinutes(notifications[0]?.social_pause_after_close) * 60 * 1000);
  };

  if (!currentNotification || !isMounted || chatOpen || !mobileAllowed) return null;

  // A posicao e os tempos continuam vindo das configuracoes sociais ja existentes.
  const position = getSocialPosition(notifications[0]?.social_position);
  const theme = getNotificationStyle(currentNotification.tipo_servico || '');
  const display = getSocialDisplay(currentNotification);

  return (
    <div
      aria-hidden={!isVisible}
      className={`ix-social-motion fixed ${socialPositionClasses[position]} z-[70] w-[360px] max-w-[calc(100vw-8px)] sm:w-[320px] sm:max-w-[calc(100vw-3rem)]`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate3d(0, 0, 0)' : position.endsWith('left')
          ? 'translate3d(calc(-100% - 48px), 0, 0)'
          : 'translate3d(calc(100% + 48px), 0, 0)',
        transition: 'transform 1000ms cubic-bezier(0.22, 1, 0.36, 1), opacity 460ms ease',
      }}
    >
      <div className="w-full overflow-hidden rounded-t-xl rounded-b-none border border-gray-100 bg-white shadow-2xl">
        <div className="relative px-3 py-3 sm:px-4 sm:py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={!isVisible}
            aria-label="Fechar notificacao social"
            className="absolute right-2 top-2 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg bg-white/85 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>

          <div className="flex min-w-0 items-start gap-2.5 sm:gap-3">
            <div className={`mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10 ${theme.circle}`}>
              <i className={`${theme.icon} ${theme.text} text-lg sm:text-xl`} aria-hidden="true"></i>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 pr-7">
                <span className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide ${theme.badge}`}>
                  <i className={theme.icon} aria-hidden="true"></i>
                  <span>{display.badge}</span>
                </span>
              </div>
              {display.identity ? (
                <p className="break-words text-[13px] font-bold leading-[1.4] text-gray-900" title={display.identity}>
                  {display.identity}
                </p>
              ) : null}
              {/* A atividade dispõe de DUAS linhas, sem empurrar o rodapé.
                  Mensagens extensas são truncadas visualmente; continuam inteiras no banco. */}
              <p
                className="mt-0.5 min-h-[36px] max-h-[36px] overflow-hidden break-words text-[12px] font-normal leading-[18px] text-gray-600"
                style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}
              >
                {renderNotificationText(display.action)}
              </p>
              {/* Uma unica linha: tempo à esquerda e tipo de seguro à direita. */}
              <div className="mt-2 flex w-full min-w-0 flex-nowrap items-center justify-between gap-1.5">
                {display.elapsed ? (
                  <span className="inline-flex flex-none items-center gap-1 whitespace-nowrap text-[10px] text-gray-500 sm:text-[11px]">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {display.elapsed}
                  </span>
                ) : <span />}
                <span
                  className={`inline-flex min-w-0 max-w-[60%] flex-shrink items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${theme.badge}`}
                  title={serviceLabels[currentNotification.tipo_servico] ?? currentNotification.tipo_servico}
                >
                  <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                    {serviceLabels[currentNotification.tipo_servico] ?? currentNotification.tipo_servico}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="h-[3px] w-full bg-gray-100" aria-hidden="true">
          <div
            className={`h-full ${theme.bar}`}
            style={{ width: `${progress}%`, transition: 'width 100ms linear' }}
          />
        </div>
      </div>
      <style>{`@media (prefers-reduced-motion: reduce) { .ix-social-motion { transition-duration: 0ms !important; } }`}</style>
    </div>
  );
}
